/**
 * Thesis API client and React Query hooks
 * Stage 3: User Belief System
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { 
  Thesis, 
  ThesesResponse, 
  CreateThesisRequest, 
  UpdateThesisRequest,
  ThesisHistoryResponse,
  ThesisComparison
} from '../types/thesis';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';

/**
 * Get authorization header with current session token
 */
async function getAuthHeader(): Promise<{ Authorization: string } | {}> {
  if (!supabase) return {};
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` };
  }
  return {};
}

/**
 * Create axios instance for thesis API
 */
const createThesisClient = async () => {
  const headers = await getAuthHeader();
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });
};

// Query keys
export const thesisKeys = {
  all: ['theses'] as const,
  byTicker: (ticker: string) => ['theses', ticker.toUpperCase()] as const,
  history: (thesisId: string) => ['thesis-history', thesisId] as const,
};

/**
 * Hook to fetch all user theses
 */
export function useTheses(ticker?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...(ticker ? thesisKeys.byTicker(ticker) : thesisKeys.all), user?.id],
    enabled: !!user,
    queryFn: async () => {
      const client = await createThesisClient();
      const params = ticker ? { ticker: ticker.toUpperCase() } : {};
      const { data } = await client.get<ThesesResponse>('/api/theses', { params });
      return data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to check if user has thesis for a specific ticker
 */
export function useThesisForTicker(ticker: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...thesisKeys.byTicker(ticker || ''), user?.id],
    queryFn: async () => {
      if (!ticker) return { theses: [], count: 0 };
      const client = await createThesisClient();
      const { data } = await client.get<ThesesResponse>('/api/theses', { 
        params: { ticker: ticker.toUpperCase() } 
      });
      return data;
    },
    enabled: !!ticker && !!user,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to create a new thesis
 */
export function useCreateThesis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (thesis: CreateThesisRequest) => {
      const client = await createThesisClient();
      const { data } = await client.post<Thesis>('/api/theses', thesis);
      return data;
    },
    onSuccess: (data) => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: thesisKeys.all });
      queryClient.invalidateQueries({ queryKey: thesisKeys.byTicker(data.ticker) });
      queryClient.invalidateQueries({ queryKey: thesisKeys.history(data.id) });
      queryClient.invalidateQueries({ queryKey: ['thesis-comparison', data.id] });
    },
  });
}

/**
 * Hook to update a thesis
 */
export function useUpdateThesis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ thesisId, updates }: { thesisId: string; updates: UpdateThesisRequest }) => {
      const client = await createThesisClient();
      const { data } = await client.patch<Thesis>(`/api/theses/${thesisId}`, updates);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: thesisKeys.all });
      queryClient.invalidateQueries({ queryKey: thesisKeys.byTicker(data.ticker) });
      queryClient.invalidateQueries({ queryKey: thesisKeys.history(data.id) });
      queryClient.invalidateQueries({ queryKey: ['thesis-comparison', data.id] });
    },
  });
}

/**
 * Hook to get thesis history
 */
export function useThesisHistory(thesisId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...thesisKeys.history(thesisId || ''), user?.id],
    queryFn: async () => {
      if (!thesisId) return { history: [], count: 0 };
      const client = await createThesisClient();
      const { data } = await client.get<ThesisHistoryResponse>(`/api/theses/${thesisId}/history`);
      return data;
    },
    enabled: !!thesisId && !!user,
  });
}

/**
 * Hook to compare thesis with current analysis (Stage 4)
 */
export function useThesisComparison(thesisId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['thesis-comparison', thesisId, user?.id],
    queryFn: async () => {
      if (!thesisId) return null;
      const client = await createThesisClient();
      const { data } = await client.get<ThesisComparison>(`/api/theses/${thesisId}/compare`);
      return data;
    },
    enabled: !!thesisId && !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

